import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FiFilter, FiSliders, FiX } from "react-icons/fi";
import Pagination from "../../../components/Pagination/Pagination";
import { useProductsByCollection } from "../../../hooks/useProductsByCollection";
import { Product } from "../../../api-client";

type SortOption = "featured" | "price-asc" | "price-desc" | "name";
type PriceRange = "all" | "under-85" | "85-90" | "over-90";

type FilterColor = {
    name: string;
    hex: string;
};

const PRICE_OPTIONS: Array<{ id: PriceRange; label: string }> = [
    { id: "all", label: "All Prices" },
    { id: "under-85", label: "Under $85" },
    { id: "85-90", label: "$85 - $90" },
    { id: "over-90", label: "Over $90" },
];

const byPriceRange = (price: number, range: PriceRange) => {
    if (range === "under-85") return price < 85;
    if (range === "85-90") return price >= 85 && price <= 90;
    if (range === "over-90") return price > 90;
    return true;
};

const hasAnySelectedColor = (product: Product, selectedColors: string[]) => {
    if (selectedColors.length === 0) return true;

    const productColorNames = (product.colors ?? []).map((color) =>
        color.name.toLowerCase(),
    );

    return selectedColors.some((selected) =>
        productColorNames.includes(selected.toLowerCase()),
    );
};

const CollectionFilterPanel = ({
    colors,
    selectedColors,
    onToggleColor,
    selectedPrice,
    onSelectPrice,
    onClear,
}: {
    colors: FilterColor[];
    selectedColors: string[];
    onToggleColor: (colorName: string) => void;
    selectedPrice: PriceRange;
    onSelectPrice: (price: PriceRange) => void;
    onClear: () => void;
}) => {
    return (
        <div className="bg-[#F2F2F3] rounded-2xl border border-[#E6E7EA] p-5">
            <div className="flex items-center justify-between border-b border-[#DCDDDF] pb-4 mb-5">
                <div className="flex items-center gap-2">
                    <FiSliders className="text-lg text-[#22232A]" />
                    <h3 className="text-3xl md:text-2xl font-semibold text-[#21232A]">Filters</h3>
                </div>
                <button
                    onClick={onClear}
                    className="text-[#FF4E1A] font-semibold hover:opacity-80 transition-opacity"
                >
                    Clear Filters
                </button>
            </div>

            <div className="space-y-7">
                <section>
                    <h4 className="text-[#21232A] text-3xl md:text-xl font-semibold mb-3">Colors</h4>
                    <div className="grid grid-cols-3 gap-2">
                        {colors.map((color) => {
                            const isSelected = selectedColors.includes(color.name);

                            return (
                                <button
                                    key={color.name}
                                    onClick={() => onToggleColor(color.name)}
                                    className={`rounded-xl border p-2 text-center transition-colors ${
                                        isSelected
                                            ? "border-[#FB2C36] bg-[#FFEDEE]"
                                            : "border-[#D4D4D8] bg-[#F8F8F8]"
                                    }`}
                                >
                                    <span
                                        className="block w-8 h-8 rounded-full mx-auto border border-black/10"
                                        style={{ backgroundColor: color.hex }}
                                    />
                                    <span className="mt-2 block text-xs text-[#52525B]">{color.name}</span>
                                </button>
                            );
                        })}
                    </div>
                    {colors.length === 0 && (
                        <p className="mt-2 text-sm text-[#71717A]">No colors available</p>
                    )}
                </section>

                <section>
                    <h4 className="text-[#21232A] text-3xl md:text-xl font-semibold mb-3">Price Range</h4>
                    <div className="space-y-2">
                        {PRICE_OPTIONS.map((option) => {
                            const isSelected = selectedPrice === option.id;

                            return (
                                <button
                                    key={option.id}
                                    onClick={() => onSelectPrice(option.id)}
                                    className={`w-full text-left rounded-xl border px-4 py-2 font-medium transition-colors ${
                                        isSelected
                                            ? "border-[#FB2C36] text-[#FB2C36] bg-white"
                                            : "border-[#D4D4D8] text-[#52525B] bg-[#F4F4F5]"
                                    }`}
                                >
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </section>
            </div>
        </div>
    );
};

export function Collection() {
    const { id } = useParams<{ id: string }>();
    const { products, loading } = useProductsByCollection(Number(id));
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [selectedColors, setSelectedColors] = useState<string[]>([]);
    const [selectedPrice, setSelectedPrice] = useState<PriceRange>("all");
    const [sortBy, setSortBy] = useState<SortOption>("featured");

    const allProducts = products;

    const colors = useMemo(() => {
        const map = new Map<string, string>();

        allProducts.forEach((product) => {
            (product.colors ?? []).forEach((color) => {
                if (!map.has(color.name)) {
                    map.set(color.name, color.color || "#E5E7EB");
                }
            });
        });

        return Array.from(map.entries()).map(([name, hex]) => ({ name, hex }));
    }, [allProducts]);

    const filteredProducts = useMemo(() => {
        return allProducts.filter((product) => {
            const matchesColor = hasAnySelectedColor(product, selectedColors);
            const matchesPrice = byPriceRange(product.price, selectedPrice);
            return matchesColor && matchesPrice;
        });
    }, [allProducts, selectedColors, selectedPrice]);

    const sortedProducts = useMemo(() => {
        const safeProducts = [...filteredProducts];

        if (sortBy === "price-asc") {
            return safeProducts.sort((a, b) => a.price - b.price);
        }

        if (sortBy === "price-desc") {
            return safeProducts.sort((a, b) => b.price - a.price);
        }

        if (sortBy === "name") {
            return safeProducts.sort((a, b) => a.name.localeCompare(b.name));
        }

        // Featured: destacar productos nuevos al inicio y mantener el resto estable.
        return safeProducts.sort((a, b) => Number(Boolean(b.new)) - Number(Boolean(a.new)));
    }, [filteredProducts, sortBy]);

    const toggleColor = (colorName: string) => {
        setSelectedColors((prev) =>
            prev.includes(colorName)
                ? prev.filter((name) => name !== colorName)
                : [...prev, colorName],
        );
    };

    const closeFilters = () => setShowMobileFilters(false);

    const clearFilters = () => {
        setSelectedColors([]);
        setSelectedPrice("all");
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#E5E5E8] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#D4D4D8] border-t-[#FB2C36]" />
                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#52525B]">Loading collection</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#E5E5E8] text-[#111217]">
            <section className="bg-black pt-14 pb-16 px-4 border-b border-white/10">
                <div className="max-w-[100rem] mx-auto text-center">
                    <h1 className="text-white font-bold text-5xl md:text-7xl leading-tight">
                        Complete <span className="text-[#FB2C36]">Collection</span>
                    </h1>
                    <p className="text-[#A1A1AA] mt-4 text-lg">
                        Discover our full range of premium hoodies in every color imaginable
                    </p>
                    <div className="mt-7 flex items-center justify-center gap-3">
                        <span className="text-[#FB2C36] text-5xl font-bold leading-none">
                            {sortedProducts.length}
                        </span>
                        <span className="text-white/90 text-xl">Premium Styles Available</span>
                    </div>
                </div>
            </section>

            <section className="bg-[#F5F5F6] border-b border-[#D4D4D8]">
                <div className="max-w-[100rem] mx-auto px-4 py-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowMobileFilters((prev) => !prev)}
                            className="inline-flex items-center gap-2 rounded-full bg-[#E9E9EC] border border-[#DDDEE2] px-4 py-2 text-sm font-semibold hover:bg-[#E2E3E7] transition-colors"
                        >
                            <FiFilter className="text-base" />
                            Filters
                        </button>
                        <span className="text-sm text-[#52525B] font-medium">
                            {sortedProducts.length} products found
                        </span>
                    </div>

                    <label className="inline-flex items-center gap-2 text-sm text-[#71717A] font-semibold">
                        Sort by:
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            className="rounded-full border border-[#D4D4D8] bg-[#ECECEF] px-4 py-2 text-[#3F3F46] font-medium"
                        >
                            <option value="featured">Featured</option>
                            <option value="price-asc">Price: Low to High</option>
                            <option value="price-desc">Price: High to Low</option>
                            <option value="name">Name</option>
                        </select>
                    </label>
                </div>
            </section>

            <section className="max-w-[100rem] mx-auto px-4 py-10">
                <div className="mt-2 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
                    <div className="hidden lg:block sticky top-6">
                        <CollectionFilterPanel
                            colors={colors}
                            selectedColors={selectedColors}
                            onToggleColor={toggleColor}
                            selectedPrice={selectedPrice}
                            onSelectPrice={setSelectedPrice}
                            onClear={clearFilters}
                        />
                    </div>

                    <div>
                        {showMobileFilters && (
                            <>
                                <div className="mb-5 hidden md:block lg:hidden">
                                    <CollectionFilterPanel
                                        colors={colors}
                                        selectedColors={selectedColors}
                                        onToggleColor={toggleColor}
                                        selectedPrice={selectedPrice}
                                        onSelectPrice={setSelectedPrice}
                                        onClear={clearFilters}
                                    />
                                </div>

                                <div className="fixed inset-0 z-50 md:hidden">
                                    <button
                                        onClick={closeFilters}
                                        className="absolute inset-0 bg-black/35"
                                        aria-label="Close filters"
                                    />

                                    <aside className="relative h-full w-[300px] max-w-[85vw] bg-[#F4F4F5] border-r border-[#D4D4D8] p-4 overflow-y-auto shadow-2xl">
                                        <div className="flex items-center justify-between border-b border-[#DCDDDF] pb-4 mb-5">
                                            <div className="flex items-center gap-2">
                                                <FiSliders className="text-lg text-[#22232A]" />
                                                <h3 className="text-3xl font-semibold text-[#21232A]">Filters</h3>
                                            </div>
                                            <button
                                                onClick={closeFilters}
                                                className="text-[#3F3F46] hover:text-black transition-colors"
                                                aria-label="Close"
                                            >
                                                <FiX className="text-2xl" />
                                            </button>
                                        </div>

                                        <CollectionFilterPanel
                                            colors={colors}
                                            selectedColors={selectedColors}
                                            onToggleColor={toggleColor}
                                            selectedPrice={selectedPrice}
                                            onSelectPrice={setSelectedPrice}
                                            onClear={clearFilters}
                                        />

                                        <button
                                            onClick={closeFilters}
                                            className="w-full mt-6 rounded-full bg-[#FB2C36] text-white font-semibold py-3 hover:bg-[#E11D2D] transition-colors"
                                        >
                                            Apply Filters
                                        </button>
                                    </aside>
                                </div>
                            </>
                        )}

                        {sortedProducts.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-24 text-center">
                                <FiFilter className="text-5xl text-[#D4D4D8] mb-4" />
                                <h3 className="text-xl font-semibold text-[#3F3F46]">No products match your filters</h3>
                                <p className="text-[#71717A] mt-2 text-sm">Try adjusting or clearing the active filters.</p>
                                <button
                                    onClick={clearFilters}
                                    className="mt-5 rounded-full bg-[#FB2C36] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#E11D2D] transition-colors"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        ) : (
                            <Pagination
                                itemsPerPage={8}
                                category=""
                                products={sortedProducts}
                            />
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}